import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import PulseLineAnimation from "./PulseLineAnimation";
import AskOrbitCard from "./AskOrbitCard";
import { supabase } from "@/integrations/supabase/client";
import { useOwnClientId } from "@/hooks/useOwnClientId";
import { useToast } from "@/hooks/use-toast";

type FoundIssue = { id: string; icon: string; title: string; signalType: string };
type FoundGroup = { page: string; topScore: number; issues: FoundIssue[] };

type WorkingItem = {
  id: string;
  icon: string;
  title: string;
  page: string;
  detail: string;
};

type CompletedDetail = {
  actionType: string;
  scoreBefore: number | null;
  scoreAfter: number | null;
  modules: Array<{ intent: string; summary: string; mutations: number; notes?: string[]; failure_reason?: string }>;
  mutationCounts: { faqs: number; links: number; sections: number; meta: number; other: number };
  sectionsAdded: Array<{ heading: string; slot?: string; item_id?: string | null }>;
  faqsAdded: Array<{ question: string; item_id?: string | null }>;
  linksAdded: Array<{ anchor: string; target: string; item_id?: string | null; paragraph_index?: number | null }>;
  metaChange: { title?: string; description?: string } | null;
};

type CompletedItem = {
  id: string;             // page_actions.id (this visit)
  ts: number;
  when: string;
  bucket: "today" | "week" | "month" | "older";
  title: string;
  page: string;           // slug
  status: string;
  found: string;
  action: React.ReactNode;
  published: string;
  snapshot: string;
  detail: CompletedDetail;
  client?: string;        // client name — only set for the cross-client Daily Activity feed
};


const SIGNAL_META: Record<string, { icon: string; title: string; detail: string }> = {
  orphaned: { icon: "🔗", title: "Needs internal links", detail: "This page has few internal links pointing to it." },
  discovered_not_indexed: { icon: "⚠️", title: "Not indexed by Google", detail: "Google has found this page but hasn't indexed it yet." },
  canonical_mismatch: { icon: "🔀", title: "Indexing conflict", detail: "This page has a conflicting canonical setting." },
  striking_distance: { icon: "🎯", title: "Close to page 1", detail: "This page is close to ranking on page 1." },
};

function metaFor(signalType: string) {
  if (SIGNAL_META[signalType]) return SIGNAL_META[signalType];
  const pretty = signalType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return { icon: "•", title: pretty, detail: pretty };
}

function pathFromUrl(url: string): string {
  try { return new URL(url).pathname || "/"; } catch { return url || "/"; }
}

const ACTION_TITLE: Record<string, { working: string; done: string; icon: string }> = {
  add_faq: { working: "Adding FAQ", done: "Added FAQ", icon: "📝" },
  add_internal_links: { working: "Adding internal links", done: "Added internal links", icon: "🔗" },
  add_section: { working: "Adding content section", done: "Added content section", icon: "➕" },
  update_meta: { working: "Updating title & meta description", done: "Updated title & meta description", icon: "🏷️" },
  page_session: { working: "Improving page", done: "Page improvement session", icon: "✨" },
  rollback: { working: "Rolling back", done: "Rolled back changes", icon: "↩️" },
};

function titlesFor(actionType: string) {
  if (ACTION_TITLE[actionType]) return ACTION_TITLE[actionType];
  const pretty = actionType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return { working: pretty, done: pretty, icon: "✨" };
}

function formatWhen(ts: number): { label: string; bucket: CompletedItem["bucket"] } {
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const y = new Date(now); y.setDate(now.getDate() - 1);
  const yesterday = d.toDateString() === y.toDateString();
  const hhmm = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  const diffDays = Math.floor((now.getTime() - ts) / 86400000);
  let bucket: CompletedItem["bucket"] = "older";
  if (sameDay) bucket = "today";
  else if (diffDays < 7) bucket = "week";
  else if (diffDays < 31) bucket = "month";
  let label: string;
  if (sameDay) label = `Today · ${hhmm}`;
  else if (yesterday) label = `Yesterday · ${hhmm}`;
  else if (diffDays < 7) label = `${diffDays} days ago · ${hhmm}`;
  else label = d.toLocaleDateString();
  return { label, bucket };
}

function actionTimestamp(row: any): number {
  const t = row?.result?.applied_at || row?.updated_at || row?.created_at;
  return t ? new Date(t).getTime() : 0;
}

function buildFound(row: any, signalsByPage: Map<string, string>): string {
  // Prefer the action's ORIGINATING reason (recorded at proposal time) over the latest signal.
  const reason = row?.proposal?.reason;
  if (typeof reason === "string" && reason.trim()) return reason.trim();
  const sig = row.page_id ? signalsByPage.get(row.page_id) : null;
  if (sig) return metaFor(sig).detail;
  if (row.action_type === "add_faq") return "Missing FAQ section";
  if (row.action_type === "add_internal_links") return "Insufficient internal links";
  if (row.action_type === "update_meta") return "Weak title and meta tags";
  if (row.action_type === "add_section") return "Thin content — missing supporting section";
  return "Improvement opportunity detected";
}

// Pull plain text out of a section body that may be a string or an array of {text, href} segments.
function sectionParagraphText(p: any): string {
  if (typeof p === "string") return p;
  if (Array.isArray(p)) return p.map((seg) => (typeof seg === "string" ? seg : seg?.text ?? "")).join("");
  return "";
}
function truncate(s: string, n: number) {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length > n ? t.slice(0, n - 1).trimEnd() + "…" : t;
}

function buildAction(row: any): React.ReactNode {
  const r = row.result || {};
  const p = row.proposal || {};
  if (row.action_type === "add_faq") {
    const added: any[] = r.faqs_added || p.faqs || [];
    const before = r.before_count ?? null;
    const after = r.after_count ?? null;
    const countPart =
      before != null && after != null
        ? `Added ${after - before} FAQ${after - before === 1 ? "" : "s"} (was ${before}, now ${after})`
        : added.length
        ? `Added ${added.length} FAQ${added.length === 1 ? "" : "s"}`
        : "Added FAQ to the page";
    const firstQ = added[0]?.question ? `: “${truncate(String(added[0].question), 90)}”` : "";
    return `${countPart}${firstQ}`;
  }
  if (row.action_type === "add_internal_links") {
    const links: any[] = r.links_added || p.links || [];
    if (!links.length) return "Inserted contextual internal links";
    return (
      <div className="space-y-1">
        <div>Inserted {links.length} internal link{links.length === 1 ? "" : "s"}</div>
        <ul className="space-y-0.5 text-[11px] text-muted-foreground">
          {links.map((l: any, i: number) => {
            const anchor = String(l.anchor_text ?? l.text ?? "link");
            const dest = String(l.target_url ?? l.target_href ?? l.href ?? "");
            return (
              <li key={i} className="break-words">
                <span className="text-foreground">“{truncate(anchor, 60)}”</span>
                {" → "}
                <span className="font-mono">{dest}</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }
  if (row.action_type === "add_section") {
    const sec = r.section_added || p.section || null;
    if (sec?.h) {
      const body = truncate(sectionParagraphText(sec.p), 140);
      return body ? `Added section: “${sec.h}” — ${body}` : `Added section: “${sec.h}”`;
    }
    return "Added a new content section";
  }
  if (row.action_type === "update_meta") {
    const m = r.after_meta || p.meta || null;
    if (m?.meta_title || m?.meta_description) {
      const t = m.meta_title ? `Title: “${truncate(String(m.meta_title), 70)}”` : "";
      const d = m.meta_description ? `Description: “${truncate(String(m.meta_description), 110)}”` : "";
      return [t, d].filter(Boolean).join(" · ");
    }
    return "Updated title and meta description";
  }
  if (row.action_type === "page_session") {
    const r2 = row.result || {};
    const mods: any[] = Array.isArray(r2.modules) ? r2.modules.filter((m: any) => (m?.mutations ?? 0) > 0) : [];
    if (mods.length) {
      return mods.map((m: any) => m.summary || m.intent).join(" · ");
    }
    return "Page session — no mutations applied";
  }
  return r.summary || "Change applied to the page";
}

function buildDetail(row: any, priorScoreByPage: Map<string, number>): CompletedDetail {
  const r = row.result || {};
  const p = row.proposal || {};
  const modules: any[] = Array.isArray(r.modules) ? r.modules : [];
  const mutations: any[] = Array.isArray(r.mutations) ? r.mutations : [];

  const counts = { faqs: 0, links: 0, sections: 0, meta: 0, other: 0 };
  const sectionsAdded: Array<{ heading: string; slot?: string; item_id?: string | null }> = [];
  const faqsAdded: Array<{ question: string; item_id?: string | null }> = [];
  const linksAdded: Array<{ anchor: string; target: string; item_id?: string | null; paragraph_index?: number | null }> = [];
  let metaChange: { title?: string; description?: string } | null = null;

  for (const m of mutations) {
    const type = String(m?.type || "");
    if (type === "add_faqs" || type === "add_faq") {
      const items: any[] = m.items || m.faqs || [];
      counts.faqs += items.length || 1;
      for (const it of items) if (it?.question) faqsAdded.push({ question: String(it.question), item_id: it.item_id ?? null });
    } else if (type === "add_internal_links" || type === "add_links" || type === "insert_link") {
      // Inline single-link mutation (insert_link) or grouped (add_links).
      const items: any[] = m.items || m.links || (type === "insert_link" ? [m] : []);
      counts.links += items.length || 1;
      for (const it of items) {
        linksAdded.push({
          anchor: String(it.anchor_text ?? it.text ?? it.anchor ?? "link"),
          target: String(it.target_url ?? it.target_href ?? it.href ?? ""),
          item_id: it.item_id ?? null,
          paragraph_index: typeof it.paragraph_index === "number" ? it.paragraph_index : null,
        });
      }
    } else if (type === "add_section" || type === "append_section" || type === "insert_section") {
      counts.sections += 1;
      const sec = m.section || m.item || m;
      const heading = sec?.h || sec?.heading || m?.heading || "";
      if (heading) sectionsAdded.push({ heading: String(heading), slot: m?.slot || sec?.slot, item_id: sec?.item_id ?? m?.item_id ?? null });
    } else if (type === "update_meta" || type === "meta" || type === "set_meta") {
      counts.meta += 1;
      metaChange = { title: m?.meta?.meta_title ?? m?.meta_title, description: m?.meta?.meta_description ?? m?.meta_description };
    } else {
      counts.other += 1;
    }
  }

  // Fallback per-action-type if no mutations array (single-action rows).
  if (row.action_type === "add_faq" && faqsAdded.length === 0) {
    const items: any[] = r.faqs_added || p.faqs || [];
    counts.faqs = items.length || counts.faqs;
    for (const it of items) if (it?.question) faqsAdded.push({ question: String(it.question), item_id: it.item_id ?? null });
  }
  if (row.action_type === "add_internal_links" && linksAdded.length === 0) {
    const items: any[] = r.links_added || p.links || [];
    counts.links = items.length || counts.links;
    for (const it of items) linksAdded.push({
      anchor: String(it.anchor_text ?? it.text ?? "link"),
      target: String(it.target_url ?? it.target_href ?? it.href ?? ""),
      item_id: it.item_id ?? null,
      paragraph_index: typeof it.paragraph_index === "number" ? it.paragraph_index : null,
    });
  }
  if (row.action_type === "add_section" && sectionsAdded.length === 0) {
    const sec = r.section_added || p.section || null;
    if (sec?.h) { counts.sections = 1; sectionsAdded.push({ heading: sec.h, slot: sec?.slot, item_id: sec?.item_id ?? null }); }
  }

  if (row.action_type === "update_meta" && !metaChange) {
    const meta = r.after_meta || p.meta || null;
    if (meta) { counts.meta = 1; metaChange = { title: meta.meta_title, description: meta.meta_description }; }
  }

  const scoreAfter = typeof r?.completeness_after?.score === "number" ? r.completeness_after.score : null;
  const scoreBefore = row.page_id ? (priorScoreByPage.get(row.page_id) ?? null) : null;

  return {
    actionType: row.action_type,
    scoreBefore,
    scoreAfter,
    modules: modules.map((m: any) => ({
      intent: String(m?.intent || ""),
      summary: String(m?.summary || ""),
      mutations: Number(m?.mutations ?? 0),
      notes: Array.isArray(m?.notes) ? m.notes : [],
      failure_reason: typeof m?.failure_reason === "string" && m.failure_reason ? m.failure_reason : undefined,
    })),
    mutationCounts: counts,
    sectionsAdded,
    faqsAdded,
    linksAdded,
    metaChange,
  };
}

function buildPublished(row: any): string {
  if (row.status === "rolled_back") return "Reverted to previous version";
  if (row.result?.platform === "lovable" || row.result?.applied_at) return "Successfully added — live on page";
  return "Live on page";
}

function buildSnapshot(row: any, versionsByAction: Map<string, string>): string {
  const sid = row.result?.snapshot_id;
  const capturedAt = versionsByAction.get(row.id);
  if (sid) {
    const short = String(sid).slice(0, 8);
    if (capturedAt) {
      const d = new Date(capturedAt);
      const when = d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
      return `Saved before changes · ${when} · ${short}…`;
    }
    return `Saved before changes · ${short}…`;
  }
  return "Saved before changes";
}

type Filter = "today" | "week" | "month" | "all";

type WorkspaceTabProps = {
  clientIds?: string[];
  clientsResolved?: boolean;
};

const WorkspaceTab = ({ clientIds = [], clientsResolved = true }: WorkspaceTabProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ownClientId } = useOwnClientId("WorkspaceTab");
  const activeClientId = ownClientId ?? clientIds[0] ?? null;
  const [filter, setFilter] = useState<Filter>("today");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedPanel, setExpandedPanel] = useState<"working" | "found" | "completed" | null>(null);
  const [visibleCount, setVisibleCount] = useState(5);
  // Bumped whenever a surgical remove or visit revert succeeds so the
  // completed-actions list reflects the new state immediately.
  const [refreshTick, setRefreshTick] = useState(0);
  const [found, setFound] = useState<FoundGroup[]>([]);
  const [foundLoading, setFoundLoading] = useState(true);
  const [working, setWorking] = useState<WorkingItem[]>([]);
  const [completed, setCompleted] = useState<CompletedItem[]>([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  // Cross-client Daily Activity feed: what the 5 AM cron did across ALL clients
  // (not scoped to ownClientId). Sourced from the operator_daily_activity RPC,
  // which is SECURITY DEFINER so it sees every client's page_session rows.
  const [feedItems, setFeedItems] = useState<CompletedItem[]>([]);

  // Manual is OFF by default. Only /workspace/manual turns it on.
  // Anything else clears stale state so the Ask Orbit pill stays hidden.
  const isManual = location.pathname === "/workspace/manual";
  useEffect(() => {
    try { localStorage.setItem("orbit_workspace_mode", isManual ? "manual" : "off"); } catch {}
    window.dispatchEvent(new CustomEvent("orbit:workspace-mode", { detail: isManual ? "manual" : "off" }));
  }, [isManual]);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    (async () => {
      setFoundLoading(true);
      const { data } = await supabase
        .from("page_opportunities")
        .select("id, signal_type, priority_score, site_pages!inner(url)")
        .eq("client_id", activeClientId)
        .order("priority_score", { ascending: false })
        .limit(500);
      if (cancelled) return;
      const groups = new Map<string, FoundGroup>();
      const seenSignals = new Map<string, Set<string>>();
      for (const r of (data || []) as any[]) {
        const page = pathFromUrl(r.site_pages?.url || "");
        const m = metaFor(r.signal_type);
        let g = groups.get(page);
        if (!g) {
          g = { page, topScore: r.priority_score ?? 0, issues: [] };
          groups.set(page, g);
          seenSignals.set(page, new Set());
        }
        const seen = seenSignals.get(page)!;
        if (seen.has(r.signal_type)) continue;
        seen.add(r.signal_type);
        g.issues.push({ id: r.id, icon: m.icon, title: m.title, signalType: r.signal_type });
        if ((r.priority_score ?? 0) > g.topScore) g.topScore = r.priority_score ?? 0;
      }
      const list = Array.from(groups.values()).sort((a, b) => b.topScore - a.topScore);
      setFound(list);
      setFoundLoading(false);
    })();
    return () => { cancelled = true; };
  }, [activeClientId]);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    (async () => {
      setActionsLoading(true);
      const { data } = await supabase
        .from("page_actions")
        .select("id, action_type, status, created_at, updated_at, page_id, proposal, result, site_pages(url)")
        .eq("client_id", activeClientId)
        .order("updated_at", { ascending: false })
        .limit(200);
      if (cancelled) return;
      const rows: any[] = data || [];

      // Latest signal per page (best-effort, for the Found field on completed rows)
      const pageIds = Array.from(new Set(rows.map((r) => r.page_id).filter(Boolean)));
      const signalsByPage = new Map<string, string>();
      if (pageIds.length) {
        const { data: sigs } = await supabase
          .from("page_signals")
          .select("page_id, signal_type, detected_at")
          .in("page_id", pageIds)
          .order("detected_at", { ascending: false });
        for (const s of sigs || []) {
          if (!signalsByPage.has(s.page_id)) signalsByPage.set(s.page_id, s.signal_type);
        }
      }

      // Snapshots: page_versions.captured_at for the rows with an action_id
      const versionsByAction = new Map<string, string>();
      const actionIds = rows.map((r) => r.id);
      if (actionIds.length) {
        const { data: vers } = await supabase
          .from("page_versions")
          .select("action_id, created_at")
          .in("action_id", actionIds);
        for (const v of vers || []) {
          if (v.action_id && !versionsByAction.has(v.action_id)) versionsByAction.set(v.action_id, v.created_at);
        }
      }

      const completedStatuses = new Set(["applied", "completed", "rolled_back"]);
      const workingStatuses = new Set(["pending", "in_progress", "generating", "applying", "proposed", "approved"]);
      // Treat any row that produced a snapshot or applied_at as completed,
      // regardless of action_type or lingering status value.
      const isCompleted = (r: any) =>
        completedStatuses.has(r.status) ||
        !!r?.result?.snapshot_id ||
        !!r?.result?.applied_at;

      const w: WorkingItem[] = rows
        .filter((r) => !isCompleted(r) && workingStatuses.has(r.status))
        .map((r) => {
          const t = titlesFor(r.action_type);
          const slug = r.proposal?.slug || pathFromUrl(r.site_pages?.url || "");
          return {
            id: r.id,
            icon: t.icon,
            title: t.working,
            page: slug,
            detail: r.status === "pending" ? "Queued…" : "Working on this page…",
          };
        });

      // Build "prior score" map per page_id by walking rows OLDEST→NEWEST
      // so that, when we hit a row, priorScoreByPage holds the previous
      // page_session's completeness_after.score (i.e. this row's "before").
      const priorScoreByPage = new Map<string, number>();
      const completedRows = rows.filter(isCompleted);
      const detailByRow = new Map<string, CompletedDetail>();
      const oldestFirst = [...completedRows].sort((a, b) => actionTimestamp(a) - actionTimestamp(b));
      for (const r of oldestFirst) {
        const d = buildDetail(r, priorScoreByPage);
        detailByRow.set(r.id, d);
        const after = r?.result?.completeness_after?.score;
        if (r.page_id && typeof after === "number") priorScoreByPage.set(r.page_id, after);
      }

      const c: CompletedItem[] = completedRows
        .map((r) => {
          const ts = actionTimestamp(r);
          const { label, bucket } = formatWhen(ts);
          const t = titlesFor(r.action_type);
          const slug = r.proposal?.slug || pathFromUrl(r.site_pages?.url || "");
          return {
            id: r.id,
            ts,
            when: label,
            bucket,
            title: t.done,
            page: slug,
            status: r.status,
            found: buildFound(r, signalsByPage),
            action: buildAction(r),
            published: buildPublished(r),
            snapshot: buildSnapshot(r, versionsByAction),
            detail: detailByRow.get(r.id)!,
          };
        })
        .sort((a, b) => b.ts - a.ts);


      setWorking(w);
      setCompleted(c);
      setActionsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [activeClientId, refreshTick]);

  // Daily Activity feed — ALL clients, every day. Independent of the active
  // client; this is the operator's whole-picture view of what the daily cron
  // added across every site. buildDetail() parses each row's result.mutations
  // into sections/FAQs/links exactly like the per-client path.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Cast: this RPC isn't in the generated types.ts yet, so avoid a typed-build break.
      const { data, error } = await (supabase.rpc as any)("operator_daily_activity", { days_back: 30 });
      if (cancelled) return;
      if (error) { setFeedItems([]); return; }
      const items: CompletedItem[] = ((data as any[]) || []).map((r) => {
        const rowLike = {
          id: r.action_id,
          action_type: r.action_type,
          result: r.result,
          proposal: r.proposal,
          site_pages: { url: r.url },
          page_id: null,
          status: "applied",
          updated_at: r.created_at,
          created_at: r.created_at,
        };
        const ts = actionTimestamp(rowLike);
        const { label, bucket } = formatWhen(ts);
        const slug = r.proposal?.slug || pathFromUrl(r.url || "");
        return {
          id: r.action_id,
          ts,
          when: label,
          bucket,
          title: "",
          page: slug,
          status: "applied",
          found: "",
          action: null,
          published: "",
          snapshot: "",
          detail: buildDetail(rowLike, new Map()),
          client: r.client_name || undefined,
        } as CompletedItem;
      }).sort((a, b) => b.ts - a.ts);
      setFeedItems(items);
    })();
    return () => { cancelled = true; };
  }, [refreshTick]);

  // Per-client counts only. Found = real opportunities; Working = in-progress
  // page_actions; Completed = applied page_actions (excluding rollbacks). No
  // cross-client or global fallbacks.
  const foundCount = found.length;
  const workingCount = working.length;
  const completedCount = completed.filter((c) => c.status !== "rolled_back").length;

  const filtered = useMemo(() => {
    if (filter === "all") return completed;
    if (filter === "today") return completed.filter((c) => c.bucket === "today");
    if (filter === "week") return completed.filter((c) => c.bucket === "today" || c.bucket === "week");
    return completed.filter((c) => c.bucket !== "older");
  }, [filter, completed]);

  useEffect(() => { setVisibleCount(5); }, [filter]);

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-[28px] sm:text-[34px] font-[800] tracking-[-0.02em] leading-tight">Workspace</h1>
          <p className="mt-1 text-[13px] sm:text-[14px] text-muted-foreground">
            OrbitRank is actively improving your website.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const next = !isManual;
              try { localStorage.setItem("orbit_workspace_mode", next ? "manual" : "off"); } catch {}
              window.dispatchEvent(new CustomEvent("orbit:workspace-mode", { detail: next ? "manual" : "off" }));
              navigate(next ? "/workspace/manual" : "/workspace");
            }}
            aria-pressed={isManual}
            className={cn(
              "flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-[700] text-foreground transition-all",
              isManual
                ? "border-emerald-500/60 bg-emerald-500/[0.08]"
                : "border-border bg-card hover:border-emerald-500/40 hover:bg-emerald-500/[0.04]"
            )}
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400 shadow-[0_0_8px_-2px_hsl(152_70%_45%/0.55)]">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
            </span>
            Manual
          </button>
        </div>
      </div>

      <section
        className="relative mb-8 overflow-hidden rounded-[16px] border p-5 sm:p-6"
        style={{
          background:
            "radial-gradient(120% 140% at 0% 0%, hsl(var(--primary) / 0.10), transparent 55%), linear-gradient(160deg, hsl(var(--card)), hsl(var(--background)))",
          borderColor: "hsl(var(--primary) / 0.4)",
          boxShadow:
            "inset 0 0 0 1px hsl(var(--primary) / 0.06), 0 0 12px -6px hsl(var(--primary) / 0.18)",
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="relative mt-1.5 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary shadow-[0_0_10px_hsl(var(--primary))]" />
            </span>
            <div>
              <div className="text-[20px] sm:text-[22px] font-[800] tracking-[-0.01em] leading-none">Workspace Active</div>
              <div className="mt-1.5 text-[12px] sm:text-[13px] text-muted-foreground">
                Last scan completed <span className="text-foreground/80">14 minutes ago</span>.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block w-[180px]"><PulseLineAnimation /></div>
            <div className="flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-[10px] font-[800] uppercase tracking-[0.12em] text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse shadow-[0_0_6px_hsl(var(--primary))]" /> Live
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricBlock
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>}
            value={String(foundCount)} title="Found" sub="items to improve" cta="View found"
            onCtaClick={() => document.getElementById('found-section')?.scrollIntoView({ behavior: 'smooth' })}
          />
          <MetricBlock
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
            value={String(workingCount)} title="Working" sub="currently working on" cta="View all working"
            onCtaClick={() => document.getElementById('working-section')?.scrollIntoView({ behavior: 'smooth' })}
          />
          <MetricBlock
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>}
            value={String(completedCount)} title="Completed" sub="improvements" cta="View completed"
            onCtaClick={() => document.getElementById('completed-section')?.scrollIntoView({ behavior: 'smooth' })}
          />
          <div className="relative overflow-hidden rounded-[12px] border border-primary/25 bg-background/40 p-4" style={{ boxShadow: "inset 0 0 0 1px hsl(var(--primary) / 0.05)" }}>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-primary/15 text-primary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-[800] leading-tight text-foreground">OrbitRank is working</div>
                <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">24/7 to improve your website</div>
              </div>
            </div>
            <svg className="mt-3 w-full" height="34" viewBox="0 0 200 34" fill="none" preserveAspectRatio="none">
              <defs>
                <linearGradient id="trendFill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="hsl(var(--primary))" stopOpacity="0.35" />
                  <stop offset="1" stopColor="hsl(var(--primary))" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M0 28 L30 24 L55 26 L80 18 L110 20 L140 12 L170 14 L200 4 L200 34 L0 34 Z" fill="url(#trendFill)" />
              <path d="M0 28 L30 24 L55 26 L80 18 L110 20 L140 12 L170 14 L200 4" stroke="hsl(var(--primary))" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="198" cy="4" r="2.5" fill="hsl(var(--primary))">
                <animate attributeName="opacity" values="1;0.4;1" dur="1.6s" repeatCount="indefinite" />
              </circle>
            </svg>
          </div>
        </div>
      </section>

      {/* Daily activity — replaces the Working/Found/Completed summary panels */}
      <DailyActivityCard items={feedItems} />
      {false && (
      <div className="mb-6 grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
        {/* WORKING */}
        <SummaryPanel title="Working" count={workingCount} accent="orange" right={
          <span className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-[800] uppercase tracking-[0.1em]" style={{ borderColor: "rgba(251,146,60,0.4)", color: "#FB923C", background: "rgba(251,146,60,0.08)" }}>
            <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: "#FB923C", boxShadow: "0 0 6px #FB923C" }} /> Live
          </span>
        }>
          {expandedPanel === "working" ? (
            <div className="animate-fade-in px-4 py-4">
              {working.length === 0 ? (
                <div className="py-10 text-center text-[12px] text-muted-foreground">Nothing in progress right now.</div>
              ) : (
                <ul className="space-y-2">
                  {working.map((w) => (
                    <li key={w.id} className="flex items-start gap-3 rounded-[10px] border border-white/[0.05] bg-white/[0.02] px-3 py-2.5">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[14px]" style={{ background: "rgba(251,146,60,0.12)" }}>{w.icon}</span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[12.5px] font-[700] text-foreground">{w.title}</div>
                        <div className="truncate text-[11px] text-muted-foreground">{w.page}</div>
                        <div className="mt-0.5 text-[10.5px]" style={{ color: "#FB923C" }}>{w.detail}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <button onClick={() => setExpandedPanel(null)} className="mt-3 inline-flex items-center gap-1 text-[11px] font-[800]" style={{ color: "#FB923C" }}>
                Collapse
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
              </button>
            </div>
          ) : (
            <div className="flex h-full min-h-[230px] flex-col items-center justify-center gap-3 px-4 py-7 text-center">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#FB923C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="animate-spin" style={{ animationDuration: "3s" }}>
                <path d="M21 12a9 9 0 1 1-3-6.7"/><polyline points="21 4 21 10 15 10"/>
              </svg>
              <div>
                <div className="text-[13px] font-[700] text-foreground">Processing improvements</div>
                <div className="mt-1 text-[11.5px] text-muted-foreground">Changes are being applied<br/>and monitored.</div>
              </div>
              <button onClick={() => setExpandedPanel("working")} className="mt-1 inline-flex items-center gap-1 text-[11px] font-[800]" style={{ color: "#FB923C" }}>
                View all working
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
            </div>
          )}
        </SummaryPanel>

        {/* FOUND */}
        <SummaryPanel title="Found" count={foundCount} accent="primary">
          {expandedPanel === "found" ? (
            <div className="animate-fade-in px-4 py-4">
              {found.length === 0 ? (
                <div className="py-10 text-center text-[12px] text-muted-foreground">No issues found.</div>
              ) : (
                <ul className="space-y-2">
                  {found.map((g) => (
                    <li key={g.page} className="rounded-[10px] border border-white/[0.05] bg-white/[0.02] px-3 py-2.5">
                      <div className="truncate text-[12px] font-[700] text-foreground">{g.page}</div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {g.issues.map((i) => <IssueTag key={i.id} signalType={i.signalType} />)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <button onClick={() => setExpandedPanel(null)} className="mt-3 inline-flex items-center gap-1 text-[11px] font-[800] text-primary">
                Collapse
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
              </button>
            </div>
          ) : (() => {
            const tally: Record<string, number> = {};
            for (const g of found) for (const i of g.issues) {
              const t = tagFor(i.signalType).label;
              tally[t] = (tally[t] || 0) + 1;
            }
            const rows = Object.entries(tally).sort((a, b) => b[1] - a[1]).slice(0, 4);
            return (
              <div className="flex h-full min-h-[230px] flex-col px-4 py-5">
                <ul className="flex-1 space-y-2.5">
                  {rows.length === 0 ? (
                    <li className="py-6 text-center text-[12px] text-muted-foreground">No issues found.</li>
                  ) : rows.map(([label, count]) => {
                    const style = Object.values(TAG_STYLES).find((s) => s.label === label) || TAG_STYLES.needs_links;
                    return (
                      <li key={label} className="flex items-center justify-between gap-3">
                        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-[600]" style={{ background: style.bg, color: style.fg, borderColor: style.border }}>{label}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] font-[700] tabular-nums text-foreground/90">{count}</span>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><polyline points="9 18 15 12 9 6"/></svg>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <button onClick={() => setExpandedPanel("found")} className="mt-3 inline-flex items-center gap-1 text-[11px] font-[800] text-primary hover:underline">
                  View all found
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </button>
              </div>
            );
          })()}
        </SummaryPanel>

        {/* COMPLETED */}
        <SummaryPanel title="Completed" count={completedCount} accent="primary">
          {expandedPanel === "completed" ? (
            <div className="animate-fade-in px-4 py-4">
              {completed.length === 0 ? (
                <div className="py-10 text-center text-[12px] text-muted-foreground">No completed work yet.</div>
              ) : (
                <ul className="space-y-2">
                  {completed.map((c) => {
                    const isOpen = expanded === c.id;
                    return (
                      <li key={c.id} className="rounded-[10px] border border-white/[0.05] bg-white/[0.02]">
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : c.id)}
                          className="flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white/[0.03]"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-[12.5px] font-[700] text-foreground">{c.title}</div>
                            <div className="truncate text-[11px] text-muted-foreground">{c.page}</div>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <span className="text-[10.5px] text-muted-foreground">{c.when}</span>
                            <svg
                              width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                              className={cn("text-muted-foreground transition-transform", isOpen && "rotate-180")}
                            >
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </div>
                        </button>
                        {isOpen && <CompletedDetails item={c} clientId={activeClientId} onChanged={() => setRefreshTick((t) => t + 1)} />}
                      </li>
                    );
                  })}
                </ul>
              )}
              <button onClick={() => setExpandedPanel(null)} className="mt-3 inline-flex items-center gap-1 text-[11px] font-[800] text-primary">
                Collapse
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
              </button>
            </div>
          ) : (
            <div className="flex h-full min-h-[230px] flex-col items-center justify-center gap-3 px-4 py-7 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full" style={{ background: "rgba(35,209,96,0.12)", border: "1.5px solid rgba(35,209,96,0.5)" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#23D160" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              <div>
                <div className="text-[13px] font-[700] text-foreground">Great progress!</div>
                <div className="mt-1 text-[11.5px] text-muted-foreground">
                  {completed.filter((c) => c.status !== "rolled_back").length} improvements have been<br/>completed and deployed.
                </div>
              </div>
              <button onClick={() => setExpandedPanel("completed")} className="inline-flex items-center gap-1 text-[11px] font-[800] text-primary hover:underline">
                View all completed
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </button>
            </div>
          )}
        </SummaryPanel>
      </div>
      )}

      <AskOrbitCard clientId={activeClientId} clientResolved={clientsResolved} />

    </div>
  );
};

// ── Daily activity feed ─────────────────────────────────────────────────────
// One card that lists what the daily engine pass (the ~5 AM 'page_session'
// runs) actually did — page touched + section added, N FAQs added, internal
// links added — grouped by day, most recent first. Fed by the cross-client
// `feedItems` (operator_daily_activity RPC), so it shows every client's daily
// activity with a client badge per row — not just the active client.
function feedSummary(d: CompletedItem["detail"]): { main: string; links: number } | null {
  const parts: string[] = [];
  if (d.sectionsAdded?.length) {
    parts.push(`added section “${truncate(d.sectionsAdded[0].heading, 46)}”`);
    if (d.sectionsAdded.length > 1) parts.push(`+${d.sectionsAdded.length - 1} more`);
  }
  if (d.faqsAdded?.length) parts.push(`${d.faqsAdded.length} FAQ${d.faqsAdded.length === 1 ? "" : "s"} added`);
  if (d.metaChange && (d.metaChange.title || d.metaChange.description)) parts.push("meta updated");
  const links = d.linksAdded?.length || 0;
  if (!parts.length && links === 0) return null;
  if (!parts.length) return { main: `${links} internal link${links === 1 ? "" : "s"} added`, links: 0 };
  return { main: parts.join(" · "), links };
}

function activityDayLabel(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Today";
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
}

const DailyActivityCard = ({ items }: { items: CompletedItem[] }) => {
  type Group = { key: string; label: string; time: string; rows: Array<{ path: string; summary: string; client?: string }> };
  const groups: Group[] = [];
  const byKey = new Map<string, Group>();
  for (const it of items) {
    if (it.status === "rolled_back") continue;
    const s = feedSummary(it.detail);
    if (!s) continue;
    const key = new Date(it.ts).toDateString();
    let g = byKey.get(key);
    if (!g) {
      g = { key, label: activityDayLabel(it.ts), time: "", rows: [] };
      byKey.set(key, g);
      groups.push(g);
    }
    // items arrive most-recent-first, so the last write per day = the earliest run time.
    g.time = new Date(it.ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const path = "/" + String(it.page || "").replace(/^\/+/, "");
    const linkSuffix = s.links > 0 ? ` (${s.links} internal link${s.links === 1 ? "" : "s"} added)` : "";
    g.rows.push({ path, summary: s.main + linkSuffix, client: it.client });
  }
  const shown = groups.slice(0, 8);

  return (
    <div className="mb-6 overflow-hidden rounded-[16px] border border-primary/25 bg-card/40 p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[16px] sm:text-[17px] font-[800] tracking-[-0.01em] text-foreground">Daily activity</div>
          <div className="mt-0.5 text-[12px] text-muted-foreground">What OrbitRank changed across all your clients, by day.</div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-[10px] font-[800] uppercase tracking-[0.12em] text-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse shadow-[0_0_6px_hsl(var(--primary))]" /> Live
        </span>
      </div>

      {shown.length === 0 ? (
        <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 text-center">
          <div className="text-[13px] font-[700] text-foreground">No changes recorded yet</div>
          <div className="text-[12px] text-muted-foreground">The next daily pass runs at ~5:00 AM and will appear here.</div>
        </div>
      ) : (
        <div className="mt-4 max-h-[340px] space-y-4 overflow-y-auto pr-1">
          {shown.map((g) => (
            <div key={g.key}>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[11px] font-[800] uppercase tracking-[0.08em] text-foreground/90">{g.label}</span>
                {g.time && <span className="text-[10px] text-muted-foreground">{g.time}</span>}
                <span className="h-px flex-1 bg-border" />
                <span className="text-[10px] text-muted-foreground">{g.rows.length} page{g.rows.length === 1 ? "" : "s"}</span>
              </div>
              <ul className="space-y-2">
                {g.rows.map((r, i) => (
                  <li key={i} className="flex items-start gap-2.5 rounded-[10px] border border-white/[0.05] bg-white/[0.02] px-3 py-2.5 text-[12.5px] leading-snug">
                    <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
                    <span className="min-w-0">
                      {r.client && (
                        <span className="mr-1.5 inline-block rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-[800] uppercase tracking-[0.06em] text-primary align-middle">
                          {r.client}
                        </span>
                      )}
                      <span className="text-foreground/90">{r.summary}</span>
                      <span className="text-muted-foreground"> {"→"} </span>
                      <span className="break-all font-mono text-[11px] text-primary/90">{r.path}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MetricBlock = ({
  icon, value, title, sub, cta, spin, accent = "primary", onCtaClick,
}: { icon: React.ReactNode; value: string; title: string; sub: string; cta: string; spin?: boolean; accent?: "primary" | "orange"; onCtaClick?: () => void }) => {
  const isOrange = accent === "orange";
  const tone = isOrange
    ? { border: "rgba(251,146,60,0.28)", innerRing: "rgba(251,146,60,0.06)", iconBg: "rgba(251,146,60,0.16)", iconFg: "#FB923C", value: "#FB923C", cta: "#FB923C", hoverBorder: "rgba(251,146,60,0.5)" }
    : { border: "hsl(var(--primary) / 0.2)", innerRing: "hsl(var(--primary) / 0.05)", iconBg: "hsl(var(--primary) / 0.15)", iconFg: "hsl(var(--primary))", value: "hsl(var(--primary))", cta: "hsl(var(--primary))", hoverBorder: "hsl(var(--primary) / 0.4)" };
  return (
    <div
      className="group relative overflow-hidden rounded-[12px] border bg-background/40 p-4 transition-all"
      style={{ borderColor: tone.border, boxShadow: `inset 0 0 0 1px ${tone.innerRing}` }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = tone.hoverBorder)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = tone.border)}
    >
      <div className="flex items-start gap-3">
        <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]", spin && "[&>svg]:animate-spin")} style={{ background: tone.iconBg, color: tone.iconFg }}>
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[26px] font-[800] leading-none tabular-nums" style={{ color: tone.value }}>{value}</div>
          <div className="mt-1.5 text-[13px] font-[700] leading-tight text-foreground">{title}</div>
          <div className="text-[11px] leading-snug text-muted-foreground">{sub}</div>
        </div>
      </div>
      <button onClick={onCtaClick} className="mt-3 inline-flex items-center gap-1 text-[11px] font-[800] hover:underline" style={{ color: tone.cta }}>
        {cta}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
      </button>
    </div>
  );
};

const SectionHeader = ({ title, count, noMargin }: { title: string; count: number; noMargin?: boolean }) => (
  <div className={cn("flex items-center gap-2", !noMargin && "mb-3")}>
    <h2 className="text-[16px] sm:text-[18px] font-[800] tracking-[-0.01em]">{title}</h2>
    <span className="rounded-full bg-muted/60 px-2 py-0.5 text-[11px] font-[700] text-muted-foreground">{count}</span>
  </div>
);

const Panel = ({
  title, count, right, children, className,
}: {
  title: string; count: number; right?: React.ReactNode;
  children: React.ReactNode; className?: string;
}) => (
  <section
    className={cn("flex flex-col overflow-hidden rounded-[16px] border", className)}
    style={{
      background: "#151922",
      borderColor: "rgba(255,255,255,0.06)",
      boxShadow: "0 1px 0 rgba(255,255,255,0.02) inset",
    }}
  >
    <header
      className="relative flex h-16 shrink-0 items-center justify-center border-b px-4"
      style={{ background: "#151922", borderColor: "rgba(255,255,255,0.06)" }}
    >
      <div className="flex items-baseline gap-2">
        <h2 className="text-[15px] font-[700] uppercase tracking-[0.14em] text-foreground/90 leading-none">{title}</h2>
        <span className="text-[15px] font-[700] leading-none tabular-nums text-primary">{count}</span>
      </div>
      {right && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          {right}
        </div>
      )}
    </header>
    <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
  </section>
);

const PriorityScore = ({ value }: { value: number }) => (
  <div
    className="flex h-9 w-11 shrink-0 items-center justify-center rounded-[8px] border text-[13px] font-[800] tabular-nums"
    style={{ background: "#3A1116", borderColor: "#6D1A24", color: "#FF5A67" }}
  >
    {value}
  </div>
);

const TAG_STYLES: Record<string, { label: string; bg: string; fg: string; border: string }> = {
  thin_content: { label: "Thin Content", bg: "#4A2E0F", fg: "#F4A940", border: "#6B4316" },
  not_indexed: { label: "Not Indexed", bg: "#4A1313", fg: "#FF6B6B", border: "#6E2323" },
  missing_faq: { label: "Missing FAQ", bg: "#311C54", fg: "#B68CFF", border: "#4A2E7C" },
  needs_links: { label: "Needs Links", bg: "#122D57", fg: "#67A7FF", border: "#21498A" },
};

function tagFor(signalType: string) {
  const s = signalType.toLowerCase();
  if (s.includes("thin") || s.includes("content")) return TAG_STYLES.thin_content;
  if (s.includes("not_indexed") || s.includes("discovered_not_indexed") || s.includes("canonical")) return TAG_STYLES.not_indexed;
  if (s.includes("faq") || s.includes("question")) return TAG_STYLES.missing_faq;
  if (s.includes("orphan") || s.includes("link") || s.includes("striking")) return TAG_STYLES.needs_links;
  return TAG_STYLES.needs_links;
}

const IssueTag = ({ signalType }: { signalType: string }) => {
  const t = tagFor(signalType);
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 text-[11px] font-[600]"
      style={{ height: 22, background: t.bg, color: t.fg, borderColor: t.border }}
    >
      {t.label}
    </span>
  );
};


const DetailRow = ({ label, value, last }: { label: string; value: React.ReactNode; last?: boolean }) => (
  <div className={cn("flex items-start justify-between gap-4 py-2", !last && "border-b border-border/40")}>
    <div className="shrink-0 text-[11px] font-[700] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="max-w-[70%] text-right text-[12px] text-foreground">{value}</div>
  </div>
);

const SummaryPanel = ({
  title, count, accent = "primary", right, children,
}: { title: string; count: number; accent?: "primary" | "orange"; right?: React.ReactNode; children: React.ReactNode }) => {
  const countColor = accent === "orange" ? "#FB923C" : "hsl(var(--primary))";
  return (
    <section className="flex flex-col overflow-hidden rounded-[14px] border" style={{ background: "#121622", borderColor: "rgba(255,255,255,0.06)" }}>
      <header className="flex h-12 shrink-0 items-center justify-between border-b px-4" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
        <div className="flex items-baseline gap-2">
          <h3 className="text-[12px] font-[800] uppercase tracking-[0.14em] text-foreground/85 leading-none">{title}</h3>
          <span className="text-[13px] font-[800] leading-none tabular-nums" style={{ color: countColor }}>{count}</span>
        </div>
        {right}
      </header>
      <div className="flex-1">{children}</div>
    </section>
  );
};

type RemoveTarget =
  | { kind: "section"; match: { heading: string } }
  | { kind: "faq"; match: { question: string } }
  | { kind: "link"; match: { anchor: string }; paragraph_index?: number | null };

const CompletedDetails = ({ item, clientId, onChanged }: { item: CompletedItem; clientId: string | null; onChanged: () => void }) => {
  const d = item.detail;
  const { toast } = useToast();
  const [busy, setBusy] = useState<string | null>(null); // key of pending op
  const slug = item.page.replace(/^\/+/, "");
  const scoreLine =
    d.scoreAfter != null
      ? d.scoreBefore != null
        ? `${d.scoreBefore.toFixed(1)} → ${d.scoreAfter.toFixed(1)}`
        : `${d.scoreAfter.toFixed(1)} (after)`
      : null;
  const summaryBits: string[] = [];
  if (d.mutationCounts.sections) summaryBits.push(`${d.mutationCounts.sections} section${d.mutationCounts.sections === 1 ? "" : "s"}`);
  if (d.mutationCounts.faqs) summaryBits.push(`${d.mutationCounts.faqs} FAQ${d.mutationCounts.faqs === 1 ? "" : "s"}`);
  if (d.mutationCounts.links) summaryBits.push(`${d.mutationCounts.links} internal link${d.mutationCounts.links === 1 ? "" : "s"}`);
  if (d.mutationCounts.meta) summaryBits.push(`meta updated`);

  // Surgical per-item removal. Preferentially targets the immutable item_id
  // stamped at insert time; for legacy items lacking item_id we fall back to
  // the action-scoped heading/question/anchor match.
  async function removeOne(opts: { key: string; item_id?: string | null; target?: RemoveTarget; label: string }) {
    if (!clientId) return;
    if (!opts.item_id && !opts.target) {
      toast({ title: "Cannot remove", description: "This item is missing an id and has no fallback match.", variant: "destructive" });
      return;
    }
    setBusy(opts.key);
    try {
      const { data, error } = await supabase.functions.invoke("site-graph-page-worker", {
        body: {
          op: "remove_item",
          client_id: clientId,
          slug,
          ...(opts.item_id ? { item_id: opts.item_id } : {}),
          ...(opts.target ? { target: { ...opts.target, action_id: item.id } } : {}),
        },
      });
      if (error) throw error;
      const ok = (data as any)?.ok === true;
      if (!ok) throw new Error((data as any)?.error ?? "remove failed");
      toast({ title: "Removed", description: opts.label });
      onChanged();
    } catch (e: any) {
      toast({ title: "Remove failed", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }

  async function revertVisit() {
    if (!clientId) return;
    if (!confirm("Revert the whole page to its state before this visit?")) return;
    setBusy("__revert__");
    try {
      const { data, error } = await supabase.functions.invoke("site-graph-page-worker", {
        body: { op: "rollback", client_id: clientId, action_id: item.id },
      });
      if (error) throw error;
      const ok = (data as any)?.ok === true;
      if (!ok) throw new Error((data as any)?.error ?? "rollback failed");
      toast({ title: "Visit reverted", description: `Page restored to pre-visit snapshot (v=${(data as any)?.version_after}).` });
      onChanged();
    } catch (e: any) {
      toast({ title: "Revert failed", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }

  const RemoveBtn = ({ k, onClick }: { k: string; onClick: () => void }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={busy !== null}
      className="ml-2 shrink-0 rounded border border-rose-500/40 px-1.5 py-0.5 text-[10px] font-[700] uppercase tracking-wider text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
    >
      {busy === k ? "Removing…" : "Remove"}
    </button>
  );

  return (
    <div className="animate-fade-in border-t border-white/[0.05] px-3 py-3 space-y-3 text-[11.5px]">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-muted-foreground">URL:</span>
        <span className="font-mono text-foreground">{item.page}</span>
        {scoreLine && (
          <>
            <span className="text-muted-foreground">· Score</span>
            <span className="font-[700] text-emerald-400">{scoreLine}</span>
          </>
        )}
        <span className="text-muted-foreground">· {item.when}</span>
      </div>

      {summaryBits.length > 0 && (
        <div>
          <div className="mb-1 text-[10.5px] font-[700] uppercase tracking-wider text-muted-foreground">What was done</div>
          <div className="text-foreground">{summaryBits.join(" · ")}</div>
        </div>
      )}

      {d.sectionsAdded.length > 0 && (
        <div>
          <div className="mb-1 text-[10.5px] font-[700] uppercase tracking-wider text-muted-foreground">Sections added</div>
          <ul className="space-y-0.5">
            {d.sectionsAdded.map((s, i) => {
              const key = `sec:${s.item_id ?? s.heading}:${i}`;
              return (
                <li key={key} className="flex items-start justify-between gap-2 text-foreground">
                  <div className="min-w-0">
                    “{s.heading}”
                    {s.slot && <span className="ml-1 text-muted-foreground">(slot={s.slot})</span>}
                  </div>
                  <RemoveBtn k={key} onClick={() => removeOne({
                    key,
                    item_id: s.item_id ?? undefined,
                    target: s.item_id ? undefined : { kind: "section", match: { heading: s.heading } },
                    label: `Section “${s.heading}”`,
                  })} />
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {d.faqsAdded.length > 0 && (
        <div>
          <div className="mb-1 text-[10.5px] font-[700] uppercase tracking-wider text-muted-foreground">FAQs added</div>
          <ul className="space-y-0.5">
            {d.faqsAdded.slice(0, 12).map((f, i) => {
              const key = `faq:${f.item_id ?? f.question}:${i}`;
              return (
                <li key={key} className="flex items-start justify-between gap-2 text-foreground">
                  <div className="min-w-0">{truncate(f.question, 140)}</div>
                  <RemoveBtn k={key} onClick={() => removeOne({
                    key,
                    item_id: f.item_id ?? undefined,
                    target: f.item_id ? undefined : { kind: "faq", match: { question: f.question } },
                    label: `FAQ “${truncate(f.question, 60)}”`,
                  })} />
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {d.linksAdded.length > 0 && (
        <div>
          <div className="mb-1 text-[10.5px] font-[700] uppercase tracking-wider text-muted-foreground">Internal links added</div>
          <ul className="space-y-0.5">
            {d.linksAdded.slice(0, 12).map((l, i) => {
              const key = `lnk:${l.item_id ?? l.anchor + "|" + l.target}:${i}`;
              return (
                <li key={key} className="flex items-start justify-between gap-2 break-words">
                  <div className="min-w-0">
                    <span className="text-foreground">“{truncate(l.anchor, 50)}”</span>
                    <span className="text-muted-foreground"> → </span>
                    <span className="font-mono text-foreground/80">{l.target}</span>
                  </div>
                  <RemoveBtn k={key} onClick={() => removeOne({
                    key,
                    item_id: l.item_id ?? undefined,
                    target: l.item_id ? undefined : { kind: "link", match: { anchor: l.anchor }, paragraph_index: l.paragraph_index },
                    label: `Link “${truncate(l.anchor, 40)}”`,
                  })} />
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {d.metaChange && (d.metaChange.title || d.metaChange.description) && (
        <div>
          <div className="mb-1 text-[10.5px] font-[700] uppercase tracking-wider text-muted-foreground">Meta updated</div>
          {d.metaChange.title && <div className="text-foreground">Title: “{truncate(d.metaChange.title, 90)}”</div>}
          {d.metaChange.description && <div className="text-foreground">Description: “{truncate(d.metaChange.description, 140)}”</div>}
        </div>
      )}

      {d.modules.length > 0 && (
        <div>
          <div className="mb-1 text-[10.5px] font-[700] uppercase tracking-wider text-muted-foreground">Modules run</div>
          <ul className="space-y-0.5">
            {d.modules.map((m, i) => {
              const failed = m.mutations === 0;
              return (
                <li key={i} className="text-foreground/90">
                  <div>
                    <span className="font-mono text-muted-foreground">{m.intent}</span>
                    {m.summary && <span> — {m.summary}</span>}
                    {m.mutations > 0 && <span className="text-emerald-400"> · {m.mutations} mutation{m.mutations === 1 ? "" : "s"}</span>}
                    {failed && <span className="text-rose-400"> · FAILED</span>}
                    {m.notes && m.notes.length > 0 && <span className="text-amber-400"> · {m.notes.join(", ")}</span>}
                  </div>
                  {m.failure_reason && (
                    <div className="mt-0.5 pl-3 text-[11.5px] text-rose-300/90 border-l-2 border-rose-500/40 ml-1">
                      Why: {m.failure_reason}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-[10.5px] text-muted-foreground">
        <span>Trigger: {item.found}</span>
        <span>·</span>
        <span>{item.published}</span>
        <span>·</span>
        <span>{item.snapshot}</span>
      </div>

      <div className="flex items-center justify-end pt-1">
        <button
          type="button"
          onClick={revertVisit}
          disabled={busy !== null || item.status === "rolled_back"}
          className="rounded border border-amber-500/50 px-2 py-1 text-[10.5px] font-[700] uppercase tracking-wider text-amber-300 hover:bg-amber-500/10 disabled:opacity-50"
        >
          {busy === "__revert__" ? "Reverting…" : item.status === "rolled_back" ? "Already reverted" : "Revert whole visit"}
        </button>
      </div>
    </div>
  );
};



export default WorkspaceTab;
